import { create } from 'zustand';
import { useAuthStore } from './authStore';
import { apiRequest } from '../utils/api';

const useCallStore = create((set, get) => ({
  // Estado de la cola
  queueEntries: [],
  queueLoading: false,
  queueError: null,

  // Estado de llamadas activas
  activeCalls: [],
  activeCallsLoading: false,
  currentCall: null,

  // Estado específico para familiares observadores
  observerAccess: {
    canObserve: false,
    activeCall: null,
    patientInfo: null,
  },

  // Estado del paciente en cola
  patientQueueStatus: {
    inQueue: false,
    position: null,
    estimatedWait: null,
    queueId: null,
  },

  // Historial de llamadas
  callHistory: [],
  historyLoading: false,

  // --- UTILIDADES DE TRANSFORMACIÓN ---

  // Transformar entrada de cola del backend al formato del frontend
  _transformQueueEntry: (entry) => ({
    ...entry,
    patientName: entry.patient
      ? `${entry.patient.first_name || ''} ${entry.patient.last_name || ''}`.trim()
      : entry.patientName || `Paciente`,
    joinedAt: entry.created_at || entry.joinedAt,
    position: entry.position,
  }),

  // Transformar llamada del backend al formato del frontend
  _transformCall: (call) => ({
    ...call,
    patientName: call.patient
      ? `${call.patient.first_name || ''} ${call.patient.last_name || ''}`.trim()
      : call.patientName || `Paciente`,
    employeeName: call.employee
      ? `${call.employee.first_name || ''} ${call.employee.last_name || ''}`.trim()
      : call.employeeName || `Acompañante`,
    startedAt: call.started_at || call.startedAt,
  }),

  // --- ACCIONES PARA LA COLA ---

  // Cargar entradas de la cola
  loadQueue: async (silent = false) => {
    if (!silent) set({ queueLoading: true, queueError: null });
    
    try {
      const data = await apiRequest('/queue/');
      const entries = (data.queue || []).map(get()._transformQueueEntry);
      set({ 
        queueEntries: entries,
        queueLoading: false,
        queueError: null,
      });
    } catch (error) {
      if (!silent) {
        set({ 
          queueLoading: false,
          queueError: error.message,
        });
      }
    }
  },

  // Actualizar cola (usado por WebSocket)
  updateQueue: (queueData) => {
    const entries = (queueData || []).map(get()._transformQueueEntry);
    set({ queueEntries: entries, queueLoading: false });
  },

  // Unirse a la cola (para pacientes)
  joinQueue: async (queueData) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/queue/join`, {
        method: 'POST',
        headers: {
          ...useAuthStore.getState().getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(queueData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error joining queue');
      }

      const data = await response.json();
      
      set({
        patientQueueStatus: {
          inQueue: true,
          position: data.position,
          estimatedWait: data.estimated_wait,
          queueId: data.queue_id,
        },
      });

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Salir de la cola
  leaveQueue: async () => {
    const { patientQueueStatus } = get();
    
    if (!patientQueueStatus.queueId) {
      return { success: false, error: 'Not in queue' };
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/queue/${patientQueueStatus.queueId}/leave`, {
        method: 'DELETE',
        headers: useAuthStore.getState().getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Error leaving queue');
      }

      set({
        patientQueueStatus: {
          inQueue: false,
          position: null,
          estimatedWait: null,
          queueId: null,
        },
      });

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Asignar siguiente llamada (para empleados)
  assignNextCall: async () => {
    try {
      const data = await apiRequest('/queue/next', {
        method: 'POST',
      });
      return { success: true, callId: data.call_id };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Asignar llamada específica
  assignSpecificCall: async (queueEntryId) => {
    try {
      const data = await apiRequest(`/queue/${queueEntryId}/assign`, {
        method: 'POST',
      });
      return { success: true, callId: data.call_id };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // --- ACCIONES PARA LLAMADAS ACTIVAS ---

  // Cargar llamadas activas
  loadActiveCalls: async (silent = false) => {
    if (!silent) set({ activeCallsLoading: true });
    
    try {
      const data = await apiRequest('/calls/active');
      const calls = (data.calls || []).map(get()._transformCall);
      set({ 
        activeCalls: calls,
        activeCallsLoading: false,
      });
    } catch (error) {
      if (!silent) set({ activeCallsLoading: false });
      console.error('Error loading active calls:', error);
    }
  },

  // Actualizar llamadas activas (usado por WebSocket)
  updateActiveCalls: (callsData) => {
    const calls = (callsData || []).map(get()._transformCall);
    set({ activeCalls: calls, activeCallsLoading: false });
  },

  // Iniciar llamada
  startCall: async (callData) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/calls/start`, {
        method: 'POST',
        headers: {
          ...useAuthStore.getState().getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(callData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error starting call');
      }

      const data = await response.json();
      
      set({ currentCall: data.call });
      return { success: true, call: data.call };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Terminar llamada
  endCall: async (callId) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/calls/${callId}/end`, {
        method: 'POST',
        headers: useAuthStore.getState().getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error ending call');
      }

      // Limpiar llamada actual si es la que terminamos
      const { currentCall } = get();
      if (currentCall?.id === callId) {
        set({ currentCall: null });
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Obtener detalles de una llamada
  getCallDetails: async (callId) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/calls/${callId}`, {
        headers: useAuthStore.getState().getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Call not found');
      }

      const data = await response.json();
      return { success: true, call: data.call };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // --- ACCIONES PARA OBSERVADORES (FAMILIARES) ---

  // Verificar acceso de observador
  checkObserverAccess: async () => {
    const user = useAuthStore.getState().user;
    
    if (user?.role !== 'family') {
      return { success: false, error: 'Access denied' };
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/family/observer-access`, {
        headers: useAuthStore.getState().getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Error checking observer access');
      }

      const data = await response.json();
      
      set({
        observerAccess: {
          canObserve: data.can_observe,
          activeCall: data.active_call,
          patientInfo: data.patient_info,
        },
      });

      return { success: true, ...data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Actualizar acceso de observador (usado por WebSocket)
  updateObserverAccess: (accessData) => {
    set({
      observerAccess: {
        ...get().observerAccess,
        ...accessData,
      },
    });
  },

  // --- ACCIONES PARA HISTORIAL ---

  // Cargar historial de llamadas
  loadCallHistory: async () => {
    set({ historyLoading: true });
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/calls/history`, {
        headers: useAuthStore.getState().getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Error loading call history');
      }

      const data = await response.json();
      const calls = (data.calls || []).map(get()._transformCall);
      set({ 
        callHistory: calls,
        historyLoading: false,
      });
    } catch (error) {
      set({ historyLoading: false });
      console.error('Error loading call history:', error);
    }
  },

  // --- TOKENS DE LIVEKIT ---

  // Obtener token para Livekit
  getCallToken: async (callId, options = {}) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/calls/token`, {
        method: 'POST',
        headers: {
          ...useAuthStore.getState().getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          call_id: callId,
          ...options,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error getting call token');
      }

      const data = await response.json();
      return { success: true, ...data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // --- UTILIDADES ---

  // Limpiar estado de error
  clearQueueError: () => {
    set({ queueError: null });
  },

  // Reiniciar estado de cola del paciente
  resetPatientQueueStatus: () => {
    set({
      patientQueueStatus: {
        inQueue: false,
        position: null,
        estimatedWait: null,
        queueId: null,
      },
    });
  },

  // Actualizar posición en cola (usado por WebSocket)
  updateQueuePosition: (position, estimatedWait) => {
    set({
      patientQueueStatus: {
        ...get().patientQueueStatus,
        position,
        estimatedWait,
      },
    });
  },

  // Marcar que la llamada ha iniciado (para pacientes)
  markCallStarted: (callData) => {
    set({
      currentCall: callData,
      patientQueueStatus: {
        inQueue: false,
        position: null,
        estimatedWait: null,
        queueId: null,
      },
    });
  },

  // Establecer la llamada actual (función genérica)
  setCurrentCall: (callData) => {
    set({ currentCall: callData });
  },

  // Obtener estadísticas rápidas
  getStats: () => {
    const { queueEntries, activeCalls } = get();
    return {
      queueLength: queueEntries.length,
      activeCallsCount: activeCalls.length,
      urgentCalls: queueEntries.filter(entry => entry.priority === 1).length,
    };
  },

  // Filtrar cola por prioridad
  getQueueByPriority: (priority) => {
    const { queueEntries } = get();
    return queueEntries.filter(entry => entry.priority === priority);
  },

  // Verificar si el usuario actual está en una llamada activa
  isUserInActiveCall: () => {
    const { activeCalls } = get();
    const user = useAuthStore.getState().user;
    
    return activeCalls.some(call => 
      call.patient_id === user?.id || call.employee_id === user?.id
    );
  },
}));

export { useCallStore };